import React from 'react';
import OwnerFilter from 'domain/sales/shared/filter/OwnerFilter';
import {
  DataGridLayout,
  DateTimeRangeFilter,
  DESC,
  Filters,
  FiltersLayout,
  List,
  ListHeader,
  ListPageLayout,
  QuickFilterAndSort,
  SearchInput,
} from 'framework/list';
import { DateField, ExpandingTooltipField, FullDateField, SearchResultField } from 'framework/ui/field';
import useOpportunityService, {
  CLOSED_LOST,
  CLOSED_WON,
  OPPORTUNITY_STAGES,
  POSTPONED,
  READ_FULL_OPPORTUNITIES_LIST,
  UPDATE_OPPORTUNITY_FROM_LIST_PAGE,
} from 'domain/sales/opportunity/opportunityService';
import CreateOpportunityButton from 'domain/sales/opportunity/list/button/CreateOpportunityButton';
import EditOpportunityButton from 'domain/sales/opportunity/shared/button/EditOpportunityButton';
import OpportunityLinkField from 'domain/sales/opportunity/shared/field/OpportunityLinkField';
import OpportunityStageField from 'domain/sales/opportunity/shared/field/OpportunityStageField';
import { AccountFilter, AccountLinkField } from 'domain/shared/account';
import OpportunityStageFilter from 'domain/sales/opportunity/list/filter/OpportunityStageFilter';
import { useAuthorization } from 'framework/authorization';
import { EmployeeLinkField } from 'domain/shared/employee';
import LastTouchCrmField from 'domain/sales/shared/field/LastTouchCrmField';

const ACTIVE_STAGES = Object.keys(OPPORTUNITY_STAGES)
  .filter(stage => stage !== CLOSED_LOST)
  .filter(stage => stage !== CLOSED_WON);

const DAYS_COLOR_BORDER = {
  greenBorder: 1.5,
  yellowBorder: 3.5,
  lightRedBorder: 7.5,
};

const EXCEED_DAYS = 21;

const NOT_ACTIVE_STATES = Object.keys(OPPORTUNITY_STAGES).filter(
  state => state === CLOSED_LOST || state === CLOSED_WON || state === POSTPONED,
);

const OpportunitiesListPage = () => {
  const { search, getFilterValidationSchema } = useOpportunityService();
  const { isGranted } = useAuthorization();

  const filters = (
    <Filters getValidationSchema={getFilterValidationSchema}>
      <FiltersLayout
        quickFilters={[
          <QuickFilterAndSort
            label="All active opportunities"
            state={{ stateStep: { in: ACTIVE_STAGES } }}
            defaultSort={{ field: 'createdAt', order: DESC }}
          />,
          <QuickFilterAndSort
            label="All won opportunities"
            state={{ stateStep: { in: [CLOSED_WON] } }}
            defaultSort={{ field: 'closeDate', order: DESC }}
          />,
          <QuickFilterAndSort
            label="All lost opportunities"
            state={{ stateStep: { in: [CLOSED_LOST] } }}
            defaultSort={{ field: 'closeDate', order: DESC }}
          />,
        ]}
      >
        <AccountFilter label="Account name" source="account.alias" />
        <OpportunityStageFilter label="State" source="stateStep" />
        <OwnerFilter label="Owner" source="owner.alias" isVisible={isGranted(READ_FULL_OPPORTUNITIES_LIST)} />
        <DateTimeRangeFilter label="Created date" source="createdAt" />
        <DateTimeRangeFilter label="Close date" source="closeDate" />
      </FiltersLayout>
    </Filters>
  );

  return (
    <List getDataMethod={search} defaultSortOrder={DESC} defaultSortField="updatedAt">
      <ListPageLayout
        header={<ListHeader label="Opportunities" icon="fa-compass" />}
        filter={filters}
        actions={[<CreateOpportunityButton />]}
        search={<SearchInput label="opportunity" />}
      >
        <DataGridLayout
          buttons={[
            <EditOpportunityButton
              source="id"
              outline
              permissionToCheck={UPDATE_OPPORTUNITY_FROM_LIST_PAGE}
              sx={{ height: '2em' }}
            />,
          ]}
          isSmallTable
        >
          <OpportunityLinkField label="ID" source="id" idSource="id" isSortable width={60} />
          <ExpandingTooltipField source="name" label="Opportunity" isSortable maxValueLength={25} width={200}>
            <SearchResultField />
          </ExpandingTooltipField>
          <AccountLinkField
            label="Account"
            source="accountName"
            sortSource="account.accountName"
            idSource="accountId"
            isSortable
            isInternal={false}
          />
          <LastTouchCrmField
            label="Last touch"
            source="updatedAt"
            statusSource="stateStep"
            colorBorders={DAYS_COLOR_BORDER}
            skipStates={NOT_ACTIVE_STATES}
            exceedDaysForStale={EXCEED_DAYS}
            isSortable
            width={100}
          />
          <OpportunityStageField label="State" source="stateStep" isSortable width={130} />
          <EmployeeLinkField source="ownerId" nameSource="ownerName" label="Owner" isSortable />
          <DateField label="Close date" source="closeDate" isSortable width={100} />
          <FullDateField label="Created date" source="createdAt" isSortable width={165} />
        </DataGridLayout>
      </ListPageLayout>
    </List>
  );
};

export default OpportunitiesListPage;
